import { useCallback, useEffect, useRef } from 'react'

import { useModal } from '@gousto-internal/citrus-react'
import { browserHistory } from 'react-router'

import { menuUnsavedChangesModalName } from '@library/menu-page'
import { queueTrackingEvent } from '@library/tracking-integration'

import { client as clientRoutes } from 'config/routes'

type NextLocation = {
  action?: string
  pathname?: string
  search?: string
  hash?: string
}

type RouteLeaveHookRouter = {
  setRouteLeaveHook?: (
    route: unknown,
    hook: (nextLocation: NextLocation) => boolean | void,
  ) => void | (() => void)
}

type PromptVariant = 'edit' | 'new'

type UseMenuUnsavedChangesModalArgs = {
  isEnabled: boolean
  route?: unknown
  router?: RouteLeaveHookRouter
  shouldBlockNavigation: boolean
  variant: PromptVariant
}

type MenuUnsavedChangesPromptShownEvent = {
  action: 'menu_unsaved_changes_prompt_shown'
  properties: {
    variant: PromptVariant
  }
}

type MenuUnsavedChangesPromptStayClickedEvent = {
  action: 'menu_unsaved_changes_prompt_stay_clicked'
  properties: {
    variant: PromptVariant
  }
}

type MenuUnsavedChangesPromptLeaveClickedEvent = {
  action: 'menu_unsaved_changes_prompt_leave_clicked'
  properties: {
    variant: PromptVariant
  }
}

const safePathPrefixes = [
  clientRoutes.checkout,
  clientRoutes['check-out'],
  clientRoutes.orderConfirmation.replace(':orderId', ''),
]

const shouldAllowNavigation = (pathname = '') =>
  safePathPrefixes.some((safePathPrefix) => pathname.startsWith(safePathPrefix))

export const useMenuUnsavedChangesModal = ({
  isEnabled,
  route,
  router,
  shouldBlockNavigation,
  variant,
}: UseMenuUnsavedChangesModalArgs) => {
  const { closeCurrentModal, openModal } = useModal()
  const hasConfirmedNavigationRef = useRef(false)
  const pendingLocationRef = useRef<NextLocation | null>(null)

  const handleStay = useCallback(() => {
    queueTrackingEvent<MenuUnsavedChangesPromptStayClickedEvent>({
      action: 'menu_unsaved_changes_prompt_stay_clicked',
      properties: {
        variant,
      },
    })
    pendingLocationRef.current = null
    closeCurrentModal()
  }, [closeCurrentModal, variant])

  const handleLeave = useCallback(() => {
    const nextLocation = pendingLocationRef.current

    queueTrackingEvent<MenuUnsavedChangesPromptLeaveClickedEvent>({
      action: 'menu_unsaved_changes_prompt_leave_clicked',
      properties: {
        variant,
      },
    })
    hasConfirmedNavigationRef.current = true
    pendingLocationRef.current = null
    closeCurrentModal()

    if (nextLocation) {
      window.setTimeout(() => {
        if (nextLocation.action === 'POP' || nextLocation.action === 'REPLACE') {
          browserHistory.replace(nextLocation)
        } else {
          browserHistory.push(nextLocation)
        }
        hasConfirmedNavigationRef.current = false
      }, 0)
    }
  }, [closeCurrentModal, variant])

  const handleBeforeLeave = useCallback(
    (nextLocation: NextLocation) => {
      if (
        !isEnabled ||
        !shouldBlockNavigation ||
        hasConfirmedNavigationRef.current ||
        shouldAllowNavigation(nextLocation?.pathname)
      ) {
        return true
      }

      pendingLocationRef.current = nextLocation
      queueTrackingEvent<MenuUnsavedChangesPromptShownEvent>({
        action: 'menu_unsaved_changes_prompt_shown',
        properties: {
          variant,
        },
      })
      openModal(menuUnsavedChangesModalName)

      return false
    },
    [isEnabled, openModal, shouldBlockNavigation, variant],
  )

  useEffect(() => {
    if (!isEnabled) {
      return undefined
    }

    const removeRouteLeaveHook = router?.setRouteLeaveHook?.(route, handleBeforeLeave)

    return () => {
      removeRouteLeaveHook?.()
    }
  }, [handleBeforeLeave, isEnabled, route, router])

  return {
    handleLeave,
    handleStay,
  }
}
